// Centralized hook to fetch the prices configured in Google Play Console.
// Returns formatted price strings (promo + full / strikethrough) for the
// one-time unlock and the monthly subscription. Falls back to a hardcoded
// safe default when not running inside the TWA or when the Digital Goods API
// fails (so web users see something coherent).

import { useEffect, useState } from 'react';
import {
  isTwaWithPlayBilling,
  getPlayProductDetails,
  PLAY_SKU_ONE_TIME,
  PLAY_SKU_ONE_TIME_FULL,
  PLAY_SKU_SUBSCRIPTION_MONTHLY,
  PLAY_SKU_SUBSCRIPTION_MONTHLY_FULL,
} from '../services/playBilling';

const FALLBACK = {
  oneTime: { promo: '1,49 €', full: '2,99 €', promoNum: 1.49, fullNum: 2.99 },
  subscription: { promo: '6,99 €/mois', full: '16,99 €/mois' },
  currency: 'EUR',
  formatCurrency: (n) => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: 'EUR', maximumFractionDigits: 2,
      }).format(n);
    } catch {
      return `${n.toFixed(2)} EUR`;
    }
  },
  inTwa: false,
  ready: true,
};

// Module-scope cache so we don't re-query Digital Goods on every page mount.
let _cache = null;
let _inflight = null;

const formatPriceObj = (priceObj) => {
  if (!priceObj || !priceObj.value) return null;
  const num = parseFloat(priceObj.value);
  if (Number.isNaN(num)) return null;
  const currency = priceObj.currency || 'EUR';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(num);
  } catch {
    return `${num.toFixed(2)} ${currency}`;
  }
};

const fetchPlayPrices = async () => {
  const inTwa = await isTwaWithPlayBilling();
  if (!inTwa) return { ...FALLBACK, inTwa: false };

  const details = await getPlayProductDetails([
    PLAY_SKU_ONE_TIME,
    PLAY_SKU_ONE_TIME_FULL,
    PLAY_SKU_SUBSCRIPTION_MONTHLY,
    PLAY_SKU_SUBSCRIPTION_MONTHLY_FULL,
  ]);

  const bySku = {};
  for (const d of details || []) {
    if (d && d.itemId) bySku[d.itemId] = d;
  }

  const oneTimePromoPrice = bySku[PLAY_SKU_ONE_TIME]?.price;
  const oneTimeFullPrice = bySku[PLAY_SKU_ONE_TIME_FULL]?.price;
  const subPromoPrice = bySku[PLAY_SKU_SUBSCRIPTION_MONTHLY]?.price;
  const subFullPrice = bySku[PLAY_SKU_SUBSCRIPTION_MONTHLY_FULL]?.price;

  const oneTimePromo = formatPriceObj(oneTimePromoPrice);
  const oneTimeFull = formatPriceObj(oneTimeFullPrice);
  const subPromo = formatPriceObj(subPromoPrice);
  const subFull = formatPriceObj(subFullPrice);
  const currency = oneTimePromoPrice?.currency || 'EUR';

  return {
    oneTime: {
      promo: oneTimePromo || FALLBACK.oneTime.promo,
      // Hide strikethrough when the "full price" SKU is missing or equal to
      // the promo price (= no active promotion).
      full: oneTimeFull && oneTimeFull !== oneTimePromo ? oneTimeFull : null,
      promoNum: oneTimePromoPrice ? parseFloat(oneTimePromoPrice.value) : null,
      fullNum: oneTimeFullPrice ? parseFloat(oneTimeFullPrice.value) : null,
    },
    subscription: {
      promo: subPromo ? `${subPromo}/mois` : FALLBACK.subscription.promo,
      full: subFull && subFull !== subPromo ? `${subFull}/mois` : null,
    },
    currency,
    formatCurrency: (n) => {
      try {
        return new Intl.NumberFormat(undefined, {
          style: 'currency', currency, maximumFractionDigits: 2,
        }).format(n);
      } catch {
        return `${n.toFixed(2)} ${currency}`;
      }
    },
    inTwa: true,
    ready: true,
  };
};

export function usePlayPrices() {
  const [state, setState] = useState(_cache || { ...FALLBACK, ready: false });

  useEffect(() => {
    if (_cache) return;
    if (!_inflight) {
      _inflight = fetchPlayPrices().then((res) => {
        _cache = res;
        return res;
      }).catch(() => {
        _cache = { ...FALLBACK, ready: true };
        return _cache;
      });
    }
    let mounted = true;
    _inflight.then((res) => {
      if (mounted) setState(res);
    });
    return () => { mounted = false; };
  }, []);

  return state;
}
